import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { nanoid } from "nanoid";
import { generateUserId } from "../utils/user";
import { askAssistant, fetchConversationsApi, validateSessionStatus, deleteSession, getSessionList, saveFeedback } from "../services/chatApi";
import { buildMessagesFromResponse, buildBusyMessage, buildSessionTransitionMessage } from "../utils/messageBuilder";
import { isShojiClient, isTaigaClient, AVATAR_ICONS } from "../utils/client-utils";
import {
  hideWidget,
  showWidget,
  enterFullScreenWidget,
  exitFullScreenWidget,
  notifyIncomingMessage,
} from "../utils/iframeMessenger";
import { useLocale } from "./LocaleProvider";

const ChatContext = createContext();

const PAGE_SIZE = 10;

export const ChatProvider = ({ children }) => {
  const { t } = useLocale();

  const [clientKey, setClientKey] = useState("");
  const [dataPrefix, setDataPrefix] = useState("");
  const [enableFeedback, setEnableFeedBack] = useState('');
  const [greeting, setGreeting] = useState("");
  const [header, setHeader] = useState("");

  const [messages, setMessages] = useState([]);
  const [ widgetLanguage, setWidgetLanguage ] = useState("ja")

  const [theme, setTheme] = useState("#6ea9d7");
  const [colorSet, setColorSet] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitFeedbackLoading, setSubmitFeedbackLoading] = useState(false);
  const [submitFeedbackError, setSubmitFeedbackError] = useState(false);
  const [submitFeedbackSuccess, setSubmitFeedbackSuccess] = useState(false);
  const [posFeedbackOptions, setPosFeedbackOptions] = useState([]);
  const [negFeedbackOptions, setNegFeedbackOptions] = useState([]);
  const [posFeedbackSuccessMessage, setPosFeedbackSuccessMessage] = useState("");
  const [negFeedbackSuccessMessage, setNegFeedbackSuccessMessage] = useState("");
  const [loadConversation, setLoadConversation] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [defaultIcon, setDefaultIcon] = useState(AVATAR_ICONS.MAIRA);
  const [disableUrlPattern, setDisableUrlPattern] = useState("/cart");
  const [disableAfterMS, setDisableAfterMS] = useState(60 * 60 * 1000); // default time 1 hour

  const [isMobile, setIsMobile] = useState();
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [sessionTransition, setSessionTransition] = useState(false);
  const [sessionSwitch, setSessionSwitch] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  const [openChatMenu, setOpenChatMenu] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionListIndex, setSessionListIndex] = useState(0);
  const [showChatList, setShowChatList] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const [disableMoreSessions, setDisableMoreSessions] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [confirmatModalTitle, setConfirmationModalTitle] = useState("");
  const [confirmationModalMessage, setConfirmationModalMessage] = useState("");

  // Replaces the two duplicated userId/sessionId <-> localStorage effect pairs.
  const [userId, setUserId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const activeSessionIdRef = useRef(sessionId);
  const timerRef = useRef();

  const shoji = useCallback(() => isShojiClient(clientKey), [clientKey]);
  const taiga = useCallback(() => isTaigaClient(clientKey), [clientKey]);
  const clearSession = () => {
    localStorage.removeItem(`${dataPrefix}_sessionId`);
    setStartIndex(0);
    setSessionId("");
    setDisableInput(false);
  };

  useEffect(() => {
    if (clientKey && shoji()) {
      setDefaultIcon(AVATAR_ICONS.SHOJI);
    } else if (clientKey && taiga()) {
      setDefaultIcon(AVATAR_ICONS.TAIGA);
    }
  }, [clientKey, shoji, taiga]);

  useEffect(() => {
    if (dataPrefix) {
      const storedUserId = localStorage.getItem(dataPrefix + '_userId');
      setUserId(storedUserId || generateUserId());

      const storedSessionId = localStorage.getItem(dataPrefix + '_sessionId');
      if (storedSessionId) {
        setSessionId(storedSessionId);
        activeSessionIdRef.current = storedSessionId;
      }
    }
  }, [dataPrefix]);

  useEffect(() => {
    if (dataPrefix) {
      localStorage.setItem(dataPrefix + '_userId', userId);
    }
  }, [userId, dataPrefix]);

  useEffect(() => {
    if (dataPrefix) {
      localStorage.setItem(dataPrefix + '_sessionId', sessionId);
      activeSessionIdRef.current = sessionId;
    }
  }, [sessionId, dataPrefix]);


  const sendMessage = (message) => {
    setMessages((prev) => prev.concat(message));
  };


  const setEnableFeedbackData = (value) => {
    if (value === "true") setEnableFeedBack(true);
    else setEnableFeedBack(false);
  }

  const setCustomTheme = (nextTheme) => setTheme(nextTheme);
  const setCustomColorSet = (nextColorSet) => setColorSet(nextColorSet);
  const incomingMessage = (count) => notifyIncomingMessage(count);

  const makeApiCall = async (message) => {
    if (error) setError("");

    // Captured before the await: if the session gets reset/changed while this
    // request is in flight, the ref will have moved on by the time we check it below.
    const requestSessionId = activeSessionIdRef.current;

    try {
      const data = await askAssistant({ clientKey, message, userId, memberId, sessionId, widgetLanguage });
      const responseSessionId = data?.detail?.session_id;

      if (responseSessionId) {
        if((!sessionId || sessionId === "") && sessions?.length > 0){
          const newSession = {
            session_id: responseSessionId,
            session_title: message,
            created_at: new Date()
          };
          setSessions((prev)=> [newSession].concat(prev));
          setSessionListIndex((prev) => prev + 1);
        }
      }

      if (activeSessionIdRef.current !== requestSessionId) {
        return;
      }
      if (responseSessionId) setSessionId(responseSessionId);

      const responseText = data?.detail?.response;
      setStartIndex((prev) => prev + 1);

      const currentMessages = buildMessagesFromResponse({
        responseText,
        requestText: message,
        conversation_id: data?.detail?.conversation_id,
        session_id: data?.detail?.session_id,
        createdAt: new Date(),
        defaultIcon,
        useShojiAvatars: shoji(),
        busyMessage: t("chat.conversation_text_error")
      });

      setMessages((prev) => prev.concat(currentMessages));
      incomingMessage(currentMessages.length);

      if(data?.detail?.blocking_type && data?.detail?.blocking_type != ""){
        setDisableInput(true);
        if (data?.detail?.blocking_type == "session_finished") {
          setSessionExpired(true);
          timerRef.current = setTimeout(() => { setNewChatModalOpen(true); }, 3000);
          setSessions(prev => prev.map(s => (s.session_id === data?.detail?.session_id ? { ...s, session_status: "finished" } : s)))
        }else if (data?.detail?.blocking_type == "max_concurrent_sessions_reached"){
          // customized UX action
        }
      }
    } catch (err) {
      setMessages((prev) =>
        prev.concat(
          buildBusyMessage({ defaultIcon, createdAt: new Date(), busyMessage: t("chat.conversation_text_error") })
        )
      );
      incomingMessage(1);
    }
  };

  const fetchConversations = async (sid, id_from_param = false) => {
    const effectiveSessionId = id_from_param ? sid : sessionId;
    // Nothing to fetch before the session is hydrated; avoid a spurious 400 from the server.
    // Still show the welcome message so a session-less user doesn't see an empty chat.
    if (!effectiveSessionId) {
      if (messages.length === 0 && greeting !== "") {
        sendMessage({
          _id: nanoid(),
          text: greeting,
          avatar: defaultIcon,
          user: "remote",
          type: "incoming",
          createdAt: new Date(),
        });
      }
      return;
    }

    try {
      const conversations = await fetchConversationsApi({
        clientKey,
        sessionId: effectiveSessionId,
        start: id_from_param ? 0 : startIndex,
        size: PAGE_SIZE,
      });

      if (!conversations.length || conversations.length === 0 ) {
        if (messages.length === 0 && greeting !== "") {
          sendMessage({
            _id: nanoid(),
            text: greeting,
            avatar: defaultIcon,
            user: "remote",
            type: "incoming",
            createdAt: new Date(),
          });
        }
        return;
      }

      const formattedMessages = conversations.reverse().flatMap((conversation) => {
        const createdAt = new Date(conversation.created_at);
        const userMsg = {
          _id: nanoid(),
          text: conversation.request_body.query,
          session_id: conversation?.session_id,
          type: "outgoing",
          user: "local",
          createdAt,
        };

        try {
          if (conversation?.blocking_type && conversation?.blocking_type !== "") {
            setSessionExpired(true);
            setDisableInput(true);
          }
          const responseText = conversation.response || conversation.response_msg;
          const replyMessages = buildMessagesFromResponse({
            responseText,
            requestText: conversation.request_body.query,
            conversation_id: conversation?.conversation_id,
            session_id: conversation?.session_id,
            createdAt,
            defaultIcon,
            useShojiAvatars: shoji(),
            busyMessage: t("chat.conversation_text_error"),
            feedback: conversation.feedback,
          });
          return [userMsg, ...replyMessages];
        } catch (err) {
          return [userMsg, buildBusyMessage({ defaultIcon, createdAt, busyMessage: t("chat.conversation_text_error") })];
        }
      });

      setStartIndex((prev) => prev + PAGE_SIZE);

      if(formattedMessages) setMessages((prev) => formattedMessages.concat(prev));
    } catch (err) {
      setError(err.response?.data?.error);
      setLoading(false);
    }
  };
  const submitFeedback = async (conversation_id, feedback_type = "positive", message = null) => {
    if (message != null) setSubmitFeedbackLoading(true);
    try {
     await saveFeedback({
        clientKey: clientKey,
        conversation_id: conversation_id,
        feedback_type: feedback_type,
        message: message
      })
      if (message != null) setSubmitFeedbackSuccess(true);
    } catch (error) {
      if (message != null) setSubmitFeedbackError(true);
    } finally {
      setTimeout(() => setSubmitFeedbackLoading(false), 1000);
    }
  }

  const deleteChat = async (session_id) => {
    if (!session_id) return;
    const response = await deleteSession({
      clientKey: clientKey,
      sessionId: session_id
    });
    setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
    setSessionListIndex((prev) => prev > 0 ? prev - 1 : 0 );
    if(sessionId === session_id){
      setSessionId("");
      startNewSession();
    }
  }

  const getPreviousChats = async () => {
    const response = await getSessionList({
      clientKey: clientKey,
      userId: userId,
      start: sessionListIndex
    });

    const detail = response?.detail?.response;
    const page = detail?.sessions ?? [];
    const totalHits = detail?.total_hits ?? 0;

    const seen = new Set(sessions.map((s) => s.session_id));
    const merged = sessions.concat(page.filter((s) => !seen.has(s.session_id)));

    setSessions(merged);
    setSessionListIndex(sessionListIndex + page.length); 
    setDisableMoreSessions(page.length === 0 || merged.length >= totalHits);
  };

  const checkSession = async () => {
    if (!sessionId) return;
    try {
      const response = await validateSessionStatus({
        clientKey,
        sessionId: sessionId,
      });

      const data = response?.detail;
      if (data?.is_active === false) {
        clearSession();
        return null;

      } else {
        return sessionId;
      }
    } catch (err) {
      return sessionId;
    }
  };

  const startNewSession = () => {
    clearTimeout(timerRef.current);
    activeSessionIdRef.current = "";
    setMessages([]);
    sendMessage({
      _id: nanoid(),
      text: greeting,
      avatar: defaultIcon,
      user: "remote",
      type: "incoming",
      createdAt: new Date(),
    });
    setSessionId("");
    setSessionExpired(false);
    setDisableInput(false);
    setStartIndex(0);
  }

  // A different user must never inherit the previous one's session/history
  // (e.g. shared browser, user A logs out and user B logs in).
  const updateIdentity = (nextUserId) => {
    if (nextUserId && userId && nextUserId !== userId) {
      clearSession();
      startNewSession();
    }
    if (nextUserId) setUserId(nextUserId);
  };

  const handleSessionTransition = (sid) => {
    activeSessionIdRef.current = sid;
    clearSession();
    setLoading(true);
    setMessages([]);
    setNewChatModalOpen(false);
  }
  const handleClickNewChat = () => {
    setSessionTransition(true);
    setNewChatModalOpen(false);
    setDisableInput(false);
  }

  return (
    <ChatContext.Provider
      value={{
        clientKey,
        setClientKey,
        isMobile,
        setIsMobile,
        messages,
        setMessages,
        sendMessage,
        hide: hideWidget,
        show: showWidget,
        enterFullScreen: enterFullScreenWidget,
        exitFullScreen: exitFullScreenWidget,
        colorSet,
        setCustomColorSet,
        theme,
        setCustomTheme,
        makeApiCall,
        setShowTypingIndicator,
        showTypingIndicator,
        fetchConversations,
        loading,
        setLoading,
        setLoadConversation,
        loadConversation,
        setGreeting,
        setDataPrefix,
        setHeader,
        header,
        shoji,
        startIndex,
        setStartIndex,
        error,
        setError,
        userId,
        setUserId,
        memberId,
        setMemberId,
        updateIdentity,
        incomingMessage,
        enableFeedback,
    setEnableFeedbackData,
        submitFeedback,
        submitFeedbackLoading,
        setSubmitFeedbackLoading,
        submitFeedbackError,
        setSubmitFeedbackError,
        setSubmitFeedbackSuccess,
        submitFeedbackSuccess,
        posFeedbackOptions,
        setPosFeedbackOptions,
        negFeedbackOptions,
        setNegFeedbackOptions,
        posFeedbackSuccessMessage,
        setPosFeedbackSuccessMessage,
        negFeedbackSuccessMessage,
        setNegFeedbackSuccessMessage,
    disableUrlPattern, setDisableUrlPattern,
    disableAfterMS, setDisableAfterMS,
        checkSession,
        sessionTransition,
        setSessionTransition,
        handleSessionTransition,
        sessionId,
        setSessionId,
        startIndex,
        clearSession,
        setMessages,
        startNewSession,
        setSessionExpired,
        sessionExpired,
        handleClickNewChat,
        setNewChatModalOpen,
        newChatModalOpen,
        openChatMenu,
        setOpenChatMenu,
        getPreviousChats,
        sessions,
        setSessions,
        deleteChat,
        showChatList,
        setShowChatList,
        setSessionListIndex,
        sessionListIndex,
        disableInput,
        setDisableInput,
        disableMoreSessions,
        setDisableMoreSessions,
        openConfirmationModal, setOpenConfirmationModal,
        confirmatModalTitle, setConfirmationModalTitle,
        confirmationModalMessage, setConfirmationModalMessage,
        activeSessionIdRef,
        setWidgetLanguage, widgetLanguage,
        sessionSwitch, setSessionSwitch
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChatContext must be within ChatProvider");
  }

  return context;
};
